import { Link } from "@tanstack/react-router";
import {
  Activity,
  Aperture,
  ArrowUpRight,
  Check,
  CircleAlert,
  ClipboardCheck,
  Copy,
  Database,
  Film,
  Image as ImageIcon,
  KeyRound,
  Menu,
  PackageCheck,
  ReceiptText,
  RefreshCw,
  Send,
  ShieldCheck,
  X
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

type DemoMode = "image" | "video";
type CopyState = "idle" | "copied" | "error";

const API_EXAMPLE = `curl --request POST '/v1/images/jobs' \\
  --header 'Authorization: Bearer mk_...' \\
  --header 'Content-Type: application/json' \\
  --header 'Idempotency-Key: campaign-frame-01' \\
  --data '{
    "model": "gpt-image-2",
    "prompt": "Editorial product photograph on a clean set",
    "mode": "text_to_image",
    "size": "2048x2048",
    "quality": "medium",
    "n": 2
  }'`;

async function copyText(value: string) {
  try {
    if (navigator.clipboard) {
      const copied = await Promise.race([
        navigator.clipboard.writeText(value).then(() => true, () => false),
        new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 500))
      ]);
      if (copied) return true;
    }
  } catch {
    // Continue to the selection-based fallback.
  }

  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, value.length);
  const handleCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    event.clipboardData.setData("text/plain", value);
    event.preventDefault();
  };
  document.addEventListener("copy", handleCopy);
  try {
    return typeof document.execCommand === "function" && document.execCommand("copy");
  } finally {
    document.removeEventListener("copy", handleCopy);
    textarea.remove();
    previousFocus?.focus();
  }
}

function LandingHeader() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const updateHeader = () => setScrolled(window.scrollY > 8);
    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, []);

  return (
    <header className="landing-header" data-scrolled={scrolled}>
      <div className="landing-header-inner">
        <Link className="landing-brand" to="/" aria-label={t("common.brand")} onClick={closeMenu}>
          <span className="landing-brand-mark" aria-hidden="true"><Aperture size={18} strokeWidth={2} /></span>
          <span className="landing-brand-name">API Media</span>
        </Link>

        <nav className="landing-nav" aria-label={t("landing.nav.primaryAria")}>
          <a className="landing-nav-link" href="#product">{t("landing.nav.capabilities")}</a>
          <a className="landing-nav-link" href="#workflow">{t("landing.nav.workflow")}</a>
          <a className="landing-nav-link" href="#api">{t("landing.nav.api")}</a>
          <a className="landing-nav-link" href="#operations">{t("landing.nav.operations")}</a>
        </nav>

        <div className="landing-header-actions">
          <LanguageSwitcher />
          <Link className="landing-button landing-button-primary landing-header-console" to="/tasks" preload={false}>
            {t("common.actions.openConsole")}
            <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
          <button
            className="landing-menu-button"
            type="button"
            aria-label={t(menuOpen ? "landing.nav.menuClose" : "landing.nav.menuOpen")}
            aria-expanded={menuOpen}
            aria-controls="landing-mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={19} aria-hidden="true" /> : <Menu size={19} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <nav
        id="landing-mobile-nav"
        className="landing-mobile-nav"
        aria-label={t("landing.nav.mobileAria")}
        data-open={menuOpen}
        hidden={!menuOpen}
      >
        <a className="landing-mobile-nav-link" href="#product" onClick={closeMenu}>{t("landing.nav.capabilities")}</a>
        <a className="landing-mobile-nav-link" href="#workflow" onClick={closeMenu}>{t("landing.nav.workflow")}</a>
        <a className="landing-mobile-nav-link" href="#api" onClick={closeMenu}>{t("landing.nav.api")}</a>
        <a className="landing-mobile-nav-link" href="#operations" onClick={closeMenu}>{t("landing.nav.operations")}</a>
        <div className="landing-mobile-language"><LanguageSwitcher /></div>
        <Link className="landing-button landing-button-primary landing-mobile-nav-cta" to="/tasks" preload={false} onClick={closeMenu}>
          {t("common.actions.openConsole")}
          <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
      </nav>
    </header>
  );
}

function HeroTaskDemo() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<DemoMode>("image");
  const [stageIndex, setStageIndex] = useState(0);
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    if (stageIndex === 3) return;
    const timer = window.setTimeout(() => setStageIndex((stage) => Math.min(stage + 1, 3)), 1500);
    return () => window.clearTimeout(timer);
  }, [runId, stageIndex]);

  const selectMode = (nextMode: DemoMode) => {
    setMode(nextMode);
    setStageIndex(0);
    setRunId((run) => run + 1);
  };

  const replay = () => {
    setStageIndex(0);
    setRunId((run) => run + 1);
  };

  const demo = mode === "image"
    ? {
        model: t("landing.demo.image.model"),
        endpoint: t("landing.demo.image.endpoint"),
        prompt: t("landing.demo.image.prompt"),
        parameters: [
          [t("landing.demo.fields.size"), t("landing.demo.image.resolution")],
          [t("landing.demo.fields.quality"), t("landing.demo.image.quality")],
          [t("landing.demo.fields.count"), t("landing.demo.image.count")]
        ],
        statusDetails: [
          t("landing.demo.image.queued"),
          t("landing.demo.image.running"),
          t("landing.demo.image.storing"),
          t("landing.demo.image.ready")
        ]
      }
    : {
        model: t("landing.demo.video.model"),
        endpoint: t("landing.demo.video.endpoint"),
        prompt: t("landing.demo.video.prompt"),
        parameters: [
          [t("landing.demo.fields.resolution"), t("landing.demo.video.resolution")],
          [t("landing.demo.fields.duration"), t("landing.demo.video.duration")],
          [t("landing.demo.fields.ratio"), t("landing.demo.video.ratio")]
        ],
        statusDetails: [
          t("landing.demo.video.queued"),
          t("landing.demo.video.running"),
          t("landing.demo.video.storing"),
          t("landing.demo.video.ready")
        ]
      };

  const statusLabels = [
    t("landing.demo.status.queued"),
    t("landing.demo.status.running"),
    t("landing.demo.status.storing"),
    t("landing.demo.status.ready")
  ];

  return (
    <div className="landing-demo" role="region" aria-label={t("landing.demo.ariaLabel")}>
      <div className="landing-demo-toolbar">
        <span className="landing-demo-title">{t("landing.demo.title")}</span>
        <div className="landing-demo-modes" role="group" aria-label={t("landing.demo.modeAria")}>
          <button
            className="landing-demo-mode"
            type="button"
            aria-pressed={mode === "image"}
            data-active={mode === "image"}
            onClick={() => selectMode("image")}
          >
            <ImageIcon size={15} aria-hidden="true" />
            {t("landing.demo.imageLabel")}
          </button>
          <button
            className="landing-demo-mode"
            type="button"
            aria-pressed={mode === "video"}
            data-active={mode === "video"}
            onClick={() => selectMode("video")}
          >
            <Film size={15} aria-hidden="true" />
            {t("landing.demo.videoLabel")}
          </button>
        </div>
        <span className="landing-demo-endpoint">{demo.endpoint}</span>
      </div>

      <div className="landing-demo-body">
        <div className="landing-demo-request">
          <div className="landing-demo-request-head">
            <span className="landing-demo-request-label">{t("landing.demo.request")}</span>
            <span className="landing-demo-model">{demo.model}</span>
          </div>
          <div className="landing-demo-prompt">
            <span className="landing-demo-field-label">{t("landing.demo.fields.prompt")}</span>
            <p className="landing-demo-prompt-text">{demo.prompt}</p>
          </div>
          <dl className="landing-demo-parameters">
            {demo.parameters.map(([label, value]) => (
              <div className="landing-demo-parameter" key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="landing-demo-status">
          <div className="landing-demo-status-head">
            <span>{t("landing.demo.response")}</span>
            <div className="landing-demo-status-actions">
              <span className="landing-demo-current-state" data-stage={stageIndex} aria-live="polite">{statusLabels[stageIndex]}</span>
              <button
                className="landing-demo-replay"
                type="button"
                aria-label={t("landing.demo.replay")}
                title={t("landing.demo.replay")}
                onClick={replay}
              >
                <RefreshCw size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
          <ol className="landing-demo-timeline" aria-label={t("landing.demo.status.ariaLabel")}>
            {statusLabels.map((label, index) => (
              <li
                className="landing-demo-stage"
                data-stage={index < stageIndex ? "complete" : index === stageIndex ? "active" : "pending"}
                aria-current={index === stageIndex ? "step" : undefined}
                key={label}
              >
                <span className="landing-demo-stage-node" aria-hidden="true">
                  {index < stageIndex || (stageIndex === 3 && index === 3) ? <Check size={12} /> : null}
                </span>
                <span className="landing-demo-stage-copy">
                  <strong>{label}</strong>
                  <small>{demo.statusDetails[index]}</small>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function ProofRail() {
  const { t } = useTranslation();

  return (
    <section className="landing-proof" aria-label={t("landing.proof.ariaLabel")}>
      <div className="landing-proof-inner">
        <div className="landing-proof-item"><strong>{t("landing.proof.modelsValue")}</strong><span>{t("landing.proof.models")}</span></div>
        <div className="landing-proof-item"><strong>{t("landing.proof.imageValue")}</strong><span>{t("landing.proof.imageOutput")}</span></div>
        <div className="landing-proof-item"><strong>{t("landing.proof.videoValue")}</strong><span>{t("landing.proof.videoDuration")}</span></div>
        <div className="landing-proof-item"><strong>{t("landing.proof.asyncValue")}</strong><span>{t("landing.proof.asyncLabel")}</span></div>
      </div>
    </section>
  );
}

function Workflow() {
  const { t } = useTranslation();

  return (
    <section className="landing-section landing-workflow" id="workflow" aria-labelledby="landing-workflow-title">
      <div className="landing-section-inner">
        <div className="landing-section-heading">
          <h2 id="landing-workflow-title">{t("landing.workflow.title")}</h2>
          <p>{t("landing.workflow.description")}</p>
        </div>
        <div className="landing-workflow-list">
          <article className="landing-workflow-step">
            <div className="landing-workflow-step-head"><span>01</span><Send size={20} aria-hidden="true" /></div>
            <h3>{t("landing.workflow.submit.title")}</h3>
            <p>{t("landing.workflow.submit.description")}</p>
          </article>
          <article className="landing-workflow-step">
            <div className="landing-workflow-step-head"><span>02</span><Activity size={20} aria-hidden="true" /></div>
            <h3>{t("landing.workflow.snapshot.title")}</h3>
            <p>{t("landing.workflow.snapshot.description")}</p>
          </article>
          <article className="landing-workflow-step">
            <div className="landing-workflow-step-head"><span>03</span><PackageCheck size={20} aria-hidden="true" /></div>
            <h3>{t("landing.workflow.process.title")}</h3>
            <p>{t("landing.workflow.process.description")}</p>
          </article>
          <article className="landing-workflow-step">
            <div className="landing-workflow-step-head"><span>04</span><ClipboardCheck size={20} aria-hidden="true" /></div>
            <h3>{t("landing.workflow.retrieve.title")}</h3>
            <p>{t("landing.workflow.retrieve.description")}</p>
          </article>
        </div>
      </div>
    </section>
  );
}

function CapabilityBands() {
  const { t } = useTranslation();

  return (
    <section className="landing-section landing-capabilities" id="product" aria-labelledby="landing-capabilities-title">
      <div className="landing-section-inner">
        <div className="landing-section-heading landing-section-heading-wide">
          <h2 id="landing-capabilities-title">{t("landing.capabilities.title")}</h2>
          <p>{t("landing.capabilities.description")}</p>
        </div>

        <article className="landing-capability-band">
          <div className="landing-capability-intro">
            <span className="landing-capability-icon" aria-hidden="true"><ImageIcon size={21} /></span>
            <div>
              <h3>{t("landing.capabilities.image.title")}</h3>
              <p>{t("landing.capabilities.image.description")}</p>
            </div>
          </div>
          <dl className="landing-capability-specs">
            <div><dt>{t("landing.capabilities.labels.model")}</dt><dd>{t("landing.capabilities.image.model")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.modes")}</dt><dd>{t("landing.capabilities.image.modes")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.resolution")}</dt><dd>{t("landing.capabilities.image.resolution")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.quality")}</dt><dd>{t("landing.capabilities.image.quality")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.outputs")}</dt><dd>{t("landing.capabilities.image.outputs")}</dd></div>
          </dl>
        </article>

        <article className="landing-capability-band">
          <div className="landing-capability-intro">
            <span className="landing-capability-icon" aria-hidden="true"><Film size={21} /></span>
            <div>
              <h3>{t("landing.capabilities.video.title")}</h3>
              <p>{t("landing.capabilities.video.description")}</p>
            </div>
          </div>
          <dl className="landing-capability-specs">
            <div><dt>{t("landing.capabilities.labels.model")}</dt><dd>{t("landing.capabilities.video.models")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.modes")}</dt><dd>{t("landing.capabilities.video.modes")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.duration")}</dt><dd>{t("landing.capabilities.video.duration")}</dd></div>
            <div><dt>{t("landing.capabilities.labels.ratio")}</dt><dd>{t("landing.capabilities.video.ratio")}</dd></div>
          </dl>
        </article>
      </div>
    </section>
  );
}

function ApiSection() {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
  }, []);

  const copyExample = async () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);

    try {
      if (!(await copyText(API_EXAMPLE))) throw new Error("Clipboard unavailable");
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }

    resetTimer.current = window.setTimeout(() => setCopyState("idle"), 2400);
  };

  const copyLabel = copyState === "copied"
    ? t("landing.marketplace.copySuccess")
    : copyState === "error"
      ? t("landing.marketplace.copyError")
      : t("landing.marketplace.copy");

  return (
    <section className="landing-section landing-api" id="api" aria-labelledby="landing-api-title">
      <div className="landing-section-inner landing-api-layout">
        <div className="landing-api-copy">
          <h2 id="landing-api-title">{t("landing.marketplace.title")}</h2>
          <p>{t("landing.marketplace.description")}</p>
          <ul className="landing-api-points">
            <li><ShieldCheck size={17} aria-hidden="true" /><span>{t("landing.marketplace.auth")}</span></li>
            <li><KeyRound size={17} aria-hidden="true" /><span>{t("landing.marketplace.idempotency")}</span></li>
          </ul>
          <div className="landing-api-endpoints">
            <div className="landing-api-endpoint">
              <span>{t("landing.marketplace.endpoints.manifest.method")}</span>
              <code>{t("landing.marketplace.endpoints.manifest.path")}</code>
              <p>{t("landing.marketplace.endpoints.manifest.description")}</p>
            </div>
            <div className="landing-api-endpoint">
              <span>{t("landing.marketplace.endpoints.validate.method")}</span>
              <code>{t("landing.marketplace.endpoints.validate.path")}</code>
              <p>{t("landing.marketplace.endpoints.validate.description")}</p>
            </div>
            <div className="landing-api-endpoint">
              <span>{t("landing.marketplace.endpoints.image.method")}</span>
              <code>{t("landing.marketplace.endpoints.image.path")}</code>
              <p>{t("landing.marketplace.endpoints.image.description")}</p>
            </div>
            <div className="landing-api-endpoint">
              <span>{t("landing.marketplace.endpoints.video.method")}</span>
              <code>{t("landing.marketplace.endpoints.video.path")}</code>
              <p>{t("landing.marketplace.endpoints.video.description")}</p>
            </div>
          </div>
        </div>

        <div className="landing-code-panel">
          <div className="landing-code-toolbar">
            <span><strong>{t("landing.marketplace.endpoints.image.method")}</strong> {t("landing.marketplace.endpoints.image.path")}</span>
            <button className="landing-code-copy" type="button" onClick={copyExample} aria-label={copyLabel} title={copyLabel} data-state={copyState}>
              {copyState === "copied"
                ? <Check size={16} aria-hidden="true" />
                : copyState === "error"
                  ? <CircleAlert size={16} aria-hidden="true" />
                  : <Copy size={16} aria-hidden="true" />}
              <span aria-live="polite">{copyLabel}</span>
            </button>
          </div>
          <pre className="landing-code" aria-label={t("landing.marketplace.codeAria")}><code>{API_EXAMPLE}</code></pre>
        </div>
      </div>
    </section>
  );
}

function Operations() {
  const { t } = useTranslation();

  return (
    <section className="landing-section landing-operations" id="operations" aria-labelledby="landing-operations-title">
      <div className="landing-section-inner">
        <div className="landing-section-heading landing-section-heading-wide">
          <h2 id="landing-operations-title">{t("landing.operations.title")}</h2>
          <p>{t("landing.operations.description")}</p>
        </div>
        <div className="landing-operations-list">
          <article className="landing-operation">
            <ShieldCheck size={21} aria-hidden="true" />
            <div><h3>{t("landing.operations.items.idempotency.title")}</h3><p>{t("landing.operations.items.idempotency.description")}</p></div>
          </article>
          <article className="landing-operation">
            <ReceiptText size={21} aria-hidden="true" />
            <div><h3>{t("landing.operations.items.pricing.title")}</h3><p>{t("landing.operations.items.pricing.description")}</p></div>
          </article>
          <article className="landing-operation">
            <Database size={21} aria-hidden="true" />
            <div><h3>{t("landing.operations.items.storage.title")}</h3><p>{t("landing.operations.items.storage.description")}</p></div>
          </article>
          <article className="landing-operation">
            <Activity size={21} aria-hidden="true" />
            <div><h3>{t("landing.operations.items.governance.title")}</h3><p>{t("landing.operations.items.governance.description")}</p></div>
          </article>
        </div>
      </div>
    </section>
  );
}

function Verification() {
  const { t } = useTranslation();

  return (
    <section className="landing-section landing-verification" id="verification" aria-labelledby="landing-verification-title">
      <div className="landing-section-inner landing-verification-layout">
        <div className="landing-verification-copy">
          <ClipboardCheck size={27} aria-hidden="true" />
          <h2 id="landing-verification-title">{t("landing.verification.title")}</h2>
          <p>{t("landing.verification.description")}</p>
          <div className="landing-verification-command">
            <span>{t("landing.verification.commandLabel")}</span>
            <code>{t("landing.verification.command")}</code>
          </div>
        </div>
        <ul className="landing-verification-list">
          <li><Check size={16} aria-hidden="true" /><span>{t("landing.verification.checks.budget")}</span></li>
          <li><Check size={16} aria-hidden="true" /><span>{t("landing.verification.checks.replay")}</span></li>
          <li><Check size={16} aria-hidden="true" /><span>{t("landing.verification.checks.accounting")}</span></li>
          <li><Check size={16} aria-hidden="true" /><span>{t("landing.verification.checks.delivery")}</span></li>
          <li className="landing-verification-note">{t("landing.verification.note")}</li>
        </ul>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="landing-footer">
      <div className="landing-footer-inner">
        <div className="landing-footer-brand">
          <span className="landing-brand-mark" aria-hidden="true"><Aperture size={18} strokeWidth={2} /></span>
          <div><strong>API Media</strong><span>{t("landing.footer.description")}</span></div>
        </div>
        <nav className="landing-footer-nav" aria-label={t("landing.footer.navAria")}>
          <a href="#workflow">{t("landing.footer.workflow")}</a>
          <a href="#api">{t("landing.footer.api")}</a>
          <Link to="/tasks" preload={false}>{t("landing.footer.console")}</Link>
        </nav>
        <span className="landing-footer-note">{t("landing.footer.copyright")}</span>
      </div>
    </footer>
  );
}

export function LandingPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.classList.add("landing-document");
    document.documentElement.lang = i18n.resolvedLanguage === "zh-CN" ? "zh-CN" : "en";
    document.title = t("landing.meta.title");
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", t("landing.meta.description"));
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", "#101412");
    return () => document.documentElement.classList.remove("landing-document");
  }, [i18n.resolvedLanguage, t]);

  return (
    <div className="landing-page">
      <a className="landing-skip" href="#landing-content">{t("common.navigation.skipToContent")}</a>
      <LandingHeader />
      <main id="landing-content" className="landing-main" tabIndex={-1}>
        <section className="landing-hero" aria-labelledby="landing-hero-title">
          <div className="landing-hero-inner">
            <div className="landing-hero-copy">
              <h1 id="landing-hero-title">API Media</h1>
              <p>{t("landing.hero.description")}</p>
              <div className="landing-hero-actions">
                <Link className="landing-button landing-button-primary landing-hero-primary" to="/tasks" preload={false}>
                  {t("landing.hero.primaryCta")}
                  <ArrowUpRight size={16} aria-hidden="true" />
                </Link>
                <a className="landing-button landing-button-secondary" href="#api">
                  {t("landing.hero.secondaryCta")}
                </a>
              </div>
            </div>
            <HeroTaskDemo />
          </div>
        </section>
        <ProofRail />
        <Workflow />
        <CapabilityBands />
        <ApiSection />
        <Operations />
        <Verification />
        <section className="landing-final-cta" aria-labelledby="landing-final-cta-title">
          <div className="landing-final-cta-inner">
            <div>
              <h2 id="landing-final-cta-title">{t("landing.finalCta.title")}</h2>
              <p>{t("landing.finalCta.description")}</p>
            </div>
            <Link className="landing-button landing-button-primary landing-final-cta-button" to="/tasks" preload={false}>
              {t("landing.finalCta.action")}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
